import {useCallback, useEffect, useRef, useState} from 'react';
import {
    cancelForecastJob,
    getLatestForecastJob,
    startForecastJob,
} from '../../../../../utils/api/aiCitationAnalysis';
import {subscribeToForecastJobStream} from './forecastJobStream';

const ACTIVE_STATUSES = new Set(['queued', 'running']);

/**
 * MINSKY-16818 (ticket 02/03): owns a forecast job's full lifecycle for one report —
 * mount/reload restore, starting a new job, live phase-event stream consumption, and
 * cancellation — so {@code Forecast.jsx} stays plain rendering/wiring.
 *
 * On mount, checks for the latest job for {@code reportId} and restores:
 * - `queued`/`running` → live progress (reattaches to the phase-event stream)
 * - `completed` → the full result
 * - `failed`/`cancelled`/none → idle (surfacing the last error for `failed`)
 *
 * Also exposes `requestContext` (the generation params the latest job was started with, whatever
 * its status) so the Generate tab's own inputs can be restored after a page refresh too — not
 * just the result. And exposes `startedAt` (ms epoch) for an in-flight job so the UI can show a
 * real elapsed-time counter, server-authoritative when available, that survives a page refresh
 * instead of resetting to 0 — so a long-running generation doesn't look stuck.
 *
 * @param {string} reportId
 */
export function useForecastJob(reportId) {
    const [status, setStatus] = useState('idle');
    const [currentPhase, setCurrentPhase] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [error, setError] = useState(null);
    const [jobId, setJobId] = useState(null);
    const [streamUnavailable, setStreamUnavailable] = useState(false);
    // Generation params (horizon/creativity/personaHandles/userInstructions/personaSuggestions)
    // the latest job for this report was started with, if any — lets the Generate tab restore
    // its own inputs after a page refresh instead of only restoring the result itself.
    const [requestContext, setRequestContext] = useState(null);
    // Flips to true once the mount-time "latest job" fetch has settled (found or not) — lets
    // callers defer actions that should only run once it's known whether a `requestContext` is
    // available to restore from (e.g. auto-suggesting personas shouldn't race the restore).
    const [contextLoaded, setContextLoaded] = useState(false);
    // When the in-flight job actually started (ms epoch), server-authoritative when known (from
    // `ForecastJob.startedAt`) — lets the Generate tab show a real elapsed-time counter that
    // survives a page refresh instead of resetting to 0, so a long-running job doesn't look stuck.
    const [startedAt, setStartedAt] = useState(null);

    const unsubscribeRef = useRef(null);
    const stopWatching = useCallback(() => {
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
    }, []);

    /** Shared "back to idle, nothing in flight" reset used by cancel/failed/cancelled outcomes. */
    const resetToIdle = useCallback((errorMessage = null) => {
        setStatus('idle');
        setCurrentPhase(null);
        setForecast(null);
        setJobId(null);
        setError(errorMessage);
        setStartedAt(null);
    }, []);

    const finishWithCompletedResult = useCallback(async () => {
        try {
            const job = await getLatestForecastJob(reportId);
            setForecast(job?.result || null);
            setStatus('completed');
            setCurrentPhase(null);
            setJobId(null);
            setStartedAt(null);
        } catch (err) {
            console.warn('[useForecastJob] failed to load completed result', err);
            resetToIdle('Failed to load the completed forecast.');
        }
    }, [reportId, resetToIdle]);

    const watchJob = useCallback((id) => {
        stopWatching();
        setStreamUnavailable(false);
        unsubscribeRef.current = subscribeToForecastJobStream(id, {
            onPhase: ({phase}) => {
                setStatus('running');
                setCurrentPhase(phase || null);
            },
            onCompleted: async () => {
                stopWatching();
                await finishWithCompletedResult();
            },
            onFailed: (payload) => {
                stopWatching();
                resetToIdle(payload?.error || 'Failed to generate forecast');
            },
            onCancelled: () => {
                stopWatching();
                resetToIdle();
            },
            // Ticket 02: the job still completes server-side — don't error, just stop showing
            // live phase text (the Generate button falls back to a static "Generating...").
            onUnavailable: () => {
                setStreamUnavailable(true);
                setCurrentPhase(null);
            },
        });
    }, [stopWatching, finishWithCompletedResult, resetToIdle]);

    // Reload-restore.
    useEffect(() => {
        if (!reportId) {
            return undefined;
        }
        let ignore = false;

        (async () => {
            try {
                const job = await getLatestForecastJob(reportId);
                if (ignore) {
                    return;
                }
                if (!job) {
                    return;
                }
                if (job.requestContext) {
                    setRequestContext(job.requestContext);
                }
                if (ACTIVE_STATUSES.has(job.status)) {
                    setJobId(job.id);
                    setStatus(job.status);
                    setCurrentPhase(job.currentPhase || null);
                    setStartedAt(job.startedAt ? new Date(job.startedAt).getTime() : Date.now());
                    watchJob(job.id);
                } else if (job.status === 'completed') {
                    setStatus('completed');
                    setForecast(job.result || null);
                } else if (job.status === 'failed') {
                    setStatus('idle');
                    setError(job.error || null);
                }
                // cancelled (or any other terminal status) → stays idle.
            } catch (err) {
                console.warn('[useForecastJob] failed to restore latest job', err);
            } finally {
                if (!ignore) setContextLoaded(true);
            }
        })();

        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId]);

    useEffect(() => stopWatching, [stopWatching]);

    const startGenerate = useCallback(async (content, options) => {
        setError(null);
        setForecast(null);
        setStatus('queued');
        setCurrentPhase('Reading your report...');
        setStartedAt(Date.now());

        try {
            const job = await startForecastJob(reportId, content, options);
            setJobId(job.id);
            watchJob(job.id);
        } catch (err) {
            resetToIdle(err.message || 'Failed to start forecast generation');
            throw err;
        }
    }, [reportId, watchJob, resetToIdle]);

    const cancel = useCallback(async () => {
        if (!jobId) {
            return;
        }
        try {
            await cancelForecastJob(jobId);
        } catch (err) {
            console.warn('[useForecastJob] failed to cancel job', err);
        } finally {
            stopWatching();
            resetToIdle();
        }
    }, [jobId, stopWatching, resetToIdle]);

    return {
        status,
        currentPhase,
        forecast,
        error,
        jobId,
        streamUnavailable,
        requestContext,
        contextLoaded,
        startedAt,
        isActive: ACTIVE_STATUSES.has(status),
        startGenerate,
        cancel,
    };
}
